import { Component, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { ToasterService } from '@ui-nuvertos/auth';
import { AssetDepreciationService } from '../services/asset-depreciation/asset-depreciation.service';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'lib-asset-depreciation-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    SelectModule,
    TagModule,
  ],
  templateUrl: './asset-depreciation-modal.html',
  styleUrls: ['./asset-depreciation-modal.css'],
})
export class AssetDepreciationModalComponent {
  visible = input<boolean>(false);
  visibleChange = output<boolean>();
  depreciationApplied = output<void>();

  isVisible = false;
  isLoading = false;
  isApplying = false;
  isDownloading = false;
  previewGenerated = false;
  errorMessage = '';

  // Run data
  runId = '';
  runNumber = '';
  totalAssets = 0;
  totalDepreciation = 0;
  status = '';

  // FY options
  selectedFinancialYear = '';
  financialYearOptions: { label: string; value: string }[] = [];

  constructor(
    private depreciationService: AssetDepreciationService,
    private toasterService: ToasterService
  ) {
    this.generateFinancialYearOptions();
  }

  ngOnChanges() {
    this.isVisible = this.visible();
    if (this.isVisible) {
      this.reset();
    }
  }

  private generateFinancialYearOptions() {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    const startYear = currentMonth >= 3 ? currentYear : currentYear - 1;

    for (let i = 0; i < 5; i++) {
      const fy = startYear - i;
      const label = `FY ${String(fy).slice(2)}-${String(fy + 1).slice(2)}`;
      this.financialYearOptions.push({ label, value: label });
    }
  }

  async generatePreview() {
    if (!this.selectedFinancialYear) return;

    this.isLoading = true;
    this.errorMessage = '';

    try {
      const response: any = await lastValueFrom(
        this.depreciationService.generatePreview(this.selectedFinancialYear)
      );

      const data = response?.data || response;
      this.runId = String(data.run_id);
      this.runNumber = data.run_number || '';
      this.totalAssets = data.total_assets || 0;
      this.totalDepreciation = data.total_depreciation || 0;
      this.status = data.status || 'preview';
      this.previewGenerated = true;
    } catch (err: any) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to generate preview';
      this.toasterService.error('Preview Failed', this.errorMessage);
    } finally {
      this.isLoading = false;
    }
  }

  async downloadExcel() {
    if (!this.runId) return;

    this.isDownloading = true;

    try {
      const blob = await lastValueFrom(
        this.depreciationService.downloadRunExcel(this.runId)
      );

      const fileName = `depreciation_${this.status}_${this.selectedFinancialYear}.xlsx`;
      this.downloadBlob(blob, fileName);
    } catch (err: any) {
      this.toasterService.error('Download Failed', 'Failed to download Excel file');
    } finally {
      this.isDownloading = false;
    }
  }

  async applyDepreciation() {
    if (!this.runId) return;

    this.isApplying = true;
    this.errorMessage = '';

    try {
      const response: any = await lastValueFrom(
        this.depreciationService.applyDepreciation(this.runId)
      );

      const data = response?.data || response;
      this.totalAssets = data.total_assets || this.totalAssets;
      this.totalDepreciation = data.total_depreciation || this.totalDepreciation;
      this.status = 'applied';

      this.toasterService.success('Depreciation Applied', `Successfully applied depreciation for ${this.totalAssets} assets`);
      this.depreciationApplied.emit();

      // Auto-download the final Excel
      await this.downloadExcel();
    } catch (err: any) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to apply depreciation';
      this.toasterService.error('Apply Failed', this.errorMessage);
    } finally {
      this.isApplying = false;
    }
  }

  private downloadBlob(blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  onClose() {
    this.isVisible = false;
    this.visibleChange.emit(false);
  }

  private reset() {
    this.previewGenerated = false;
    this.errorMessage = '';
    this.runId = '';
    this.runNumber = '';
    this.totalAssets = 0;
    this.totalDepreciation = 0;
    this.status = '';
    this.selectedFinancialYear = '';
  }
}
